/**
 * Module dependencies.
 */

import { Balls } from './balls';
import {
  CTAWrapper,
  CarouselWrapper,
  HomeCarouselWrapper,
  WhiteContainedButton,
} from './home-carousel.style';

import { HomeGallery } from 'types/home-gallery';
import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'components/atoms/link/link';
import { Slide } from './slide';
import { getAsset } from 'utils/resolve-admin';
import { pageComponents } from 'constants/page-components';
import { useTranslation } from 'next-i18next';

/**
 * `Props` type.
 */

type Props = {
  homegallery: HomeGallery[];
};

/**
 * Get server side props.
 */

export function getServerSideProps() {
  return {
    props: { data: 'foo' },
  };
}

/**
 * Export `HomeCarousell` component.
 */

export function HomeCarousell({ homegallery }: Props): JSX.Element {
  const [translate, i18n] = useTranslation();
  const texts = useMemo(() => {
    return (homegallery ?? []).map((item) => {
      const lang = i18n.language;

      return {
        category:
          item.category[lang] ??
          item.category[i18n.options.fallbackLng as string],
        color: item.color,
        description:
          item.description[lang] ??
          item.description[i18n.options.fallbackLng as string],
        src: getAsset(item.image?.filename_disk),
        title:
          item.title[lang] ?? item.title[i18n.options.fallbackLng as string],
      };
    });
  }, [homegallery, i18n.language, i18n.options.fallbackLng]);

  const [slide, setSlide] = useState(0);
  const [position, setPosition] = useState<DOMRect | null>(null);
  const updatePosition = useCallback(
    (rect: DOMRect) => {
      if (!position || position.height < rect.height) {
        setPosition(rect);
      }

      if (
        position &&
        position?.height === rect.height &&
        (position?.top !== rect.top || position.left !== rect.left)
      ) {
        setPosition(rect);
      }
    },
    [position],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSlide(slide + 1);
    }, 3000);

    return () => {
      clearTimeout(timer);
    };
  }, [slide]);

  return (
    <>
      <HomeCarouselWrapper>
        <CarouselWrapper>
          {texts.map((text, index) => (
            <Slide
              category={text.category}
              color={text.color}
              current={slide}
              description={text.description}
              imageSrc={text.src}
              key={index}
              max={texts.length}
              onContentPosition={updatePosition}
              slideIndex={index}
              title={text.title}
            >
              <>
                {index === texts.length - 1 &&
                  position &&
                  position.height > 0 && (
                    <CTAWrapper
                      style={{
                        left: position.left,
                        top: position.top + position.height,
                        width: position.width,
                      }}
                    >
                      <Link href={pageComponents.contacts}>
                        <WhiteContainedButton
                          size={'large'}
                          variant={'contained'}
                        >
                          {translate('home.contactUsButton')}
                        </WhiteContainedButton>
                      </Link>

                      <Balls
                        current={slide}
                        onPress={(current) => setSlide(current)}
                        size={texts.length}
                      />
                    </CTAWrapper>
                  )}
              </>
            </Slide>
          ))}
        </CarouselWrapper>
      </HomeCarouselWrapper>
    </>
  );
}
